import { classes } from 'html-classes';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Typography } from '@workspace/ui/components/Typography';
import { Arrow16 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { cfSrcForImage } from '~/components/CategoryBlocks/utils';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '../Carousel/Carousel';
import type { ProductCarouselDesktopProps } from '../Carousel/interfaces';
import { CategoryBlockTitle } from '../CategoryBlocks/shared/CategoryBlockTitle';
import { IconButton } from '../IconButton';
import { BackgroundLayer } from './BackgroundLayer';

/**
 * Desktop product carousel.
 *
 * `background` sits as the first child of `Carousel`, whose root is already
 * `position: relative` and whose embla ref lives on `CarouselContent` — so the
 * layer neither scrolls with the slides nor gets clipped by the viewport.
 * `isolate` is what keeps its negative z-index above the root's own background
 * colour.
 *
 * Loading and loaded share one element tree: the skeleton replaces the slides
 * inside the same `CarouselContent`, and the header renders in both states. The
 * title and the view-all control are known before the products are, and the
 * header sits above the carousel box rather than inside it, so rendering the
 * skeleton on its own used to shift everything below the block by the height of
 * the box and the header together. Keeping one tree also stops React from
 * remounting the background image when the products arrive.
 */
const ProductCarouselDesktop = ({
  title,
  linkTo,
  skeletonComponent,
  categoryId,
  items,
  slideClassName,
  contentClassName,
  carouselClassName,
  navigationVariant = 'default',
  background,
  headerImage,
  className,
  ...props
}: ProductCarouselDesktopProps) => {
  const { t } = useTranslation();
  const { handleClickCategory } = useCategoryAnalytics();
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(true);
  const [arrowsNeeded, setArrowsNeeded] = useState(false);

  const updateArrowsState = useCallback((carouselApi: CarouselApi | null) => {
    if (!carouselApi) {
      setArrowsNeeded(false);
      return;
    }

    const canScroll =
      carouselApi.canScrollPrev() || carouselApi.canScrollNext();
    setArrowsNeeded(canScroll);
  }, []);

  const isPrevArrowVisible = useCallback(
    () => arrowsNeeded && !isBeginning,
    [arrowsNeeded, isBeginning],
  );

  const isNextArrowVisible = useCallback(
    () => arrowsNeeded && !isEnd,
    [arrowsNeeded, isEnd],
  );

  const onClickNext = useCallback(() => {
    if (!api) return;

    const currentIndex = api.selectedScrollSnap();
    const slideCount = api.slideNodes().length;

    if (currentIndex < slideCount - 1) {
      api.scrollTo(currentIndex + 1);
    } else {
      api.scrollNext();
    }
  }, [api]);

  const onClickPrev = useCallback(() => {
    if (!api) return;

    const currentIndex = api.selectedScrollSnap();

    if (currentIndex > 0) {
      api.scrollTo(currentIndex - 1);
    } else {
      api.scrollPrev();
    }
  }, [api]);

  const handleSlideChange = useCallback(() => {
    if (!api) return;
    setIsBeginning(!api.canScrollPrev());
    setIsEnd(!api.canScrollNext());
    updateArrowsState(api);
  }, [api, updateArrowsState]);

  const handleResize = useCallback(() => {
    if (!api) return;
    updateArrowsState(api);
  }, [api, updateArrowsState]);

  const headerNavigation = useMemo(() => {
    if (navigationVariant !== 'header' || !arrowsNeeded) {
      return null;
    }

    const commonProps = {
      icon: company.match({
        mcosmetic: undefined,
        default: 'chevron-3.1',
      } as const),
      iconSize: 14,
      size: company.match({
        mcosmetic: 'M',
        default: 'L',
      } as const),
      variant: company.match({
        mcosmetic: 'outline',
        default: 'shadowS',
      } as const),
    } as const;

    const icon = company.match({
      mcosmetic: <Arrow16 className="text-icon-primary" />,
    });

    return (
      <div className="flex items-center gap-2">
        <IconButton
          {...commonProps}
          onClick={onClickPrev}
          disabled={isBeginning}
          className="h-12 w-12"
        >
          {icon}
        </IconButton>
        <IconButton
          {...commonProps}
          onClick={onClickNext}
          disabled={isEnd}
          className="h-12 w-12 [&>svg]:rotate-180"
        >
          {icon}
        </IconButton>
      </div>
    );
  }, [
    navigationVariant,
    arrowsNeeded,
    onClickPrev,
    isBeginning,
    onClickNext,
    isEnd,
  ]);

  const initializeCarouselArrowsDebounced = useMemo(
    () =>
      debounce(() => {
        if (!api) {
          setIsBeginning(false);
          setIsEnd(false);
          setArrowsNeeded(false);
          return;
        }

        setIsBeginning(!api.canScrollPrev());
        setIsEnd(!api.canScrollNext());
        updateArrowsState(api);
      }, 100),
    [api, updateArrowsState],
  );

  useEffect(() => {
    if (!api) return;

    handleSlideChange();
    api.on('select', handleSlideChange);
    api.on('reInit', handleSlideChange);

    const resizeObserver = new ResizeObserver(handleResize);
    const containerNode = api.containerNode();
    if (containerNode) {
      resizeObserver.observe(containerNode);
    }

    initializeCarouselArrowsDebounced();

    return () => {
      api.off('select', handleSlideChange);
      api.off('reInit', handleSlideChange);
      resizeObserver.disconnect();
    };
  }, [api, handleSlideChange, handleResize, initializeCarouselArrowsDebounced]);

  const showHeader = !!title || !!linkTo;

  const ViewAllComponent = company.match({
    mcosmetic: Typography.Text200Semibold,
    default: Typography.Text300Semibold,
  });

  const header = showHeader && (
    <div
      className={cn(
        'flex items-center justify-between',
        company.match({
          mcosmetic: 'mb-8',
          default: 'mb-10',
        }),
      )}
    >
      <div className="flex items-center">
        {title && <CategoryBlockTitle>{title}</CategoryBlockTitle>}
      </div>
      <div className="flex items-center gap-4">
        {linkTo && (
          <Link
            to={linkTo}
            className={cn(
              'cursor-pointer text-text-secondary no-underline transition-colors hover:text-text-primary hover:no-underline',
              company.match({
                mcosmetic:
                  'outline! rounded-full px-4 py-3 outline-stroke-primary! transition-colors duration-300 hover:bg-surface-transparent-2',
              }),
            )}
            onClick={categoryId ? handleClickCategory(categoryId) : undefined}
          >
            <ViewAllComponent as="span" className="block">
              {t('viewAll')}
            </ViewAllComponent>
          </Link>
        )}
        {headerNavigation}
      </div>
    </div>
  );

  const carouselBoxClass = cn(
    '-my-2 w-full py-2 [&_.carousel-item:last-child]:mr-0!',
    background && 'isolate',
    carouselClassName,
  );

  const isLoading = !!skeletonComponent && items.length === 0;

  return (
    <div {...props} className={classes(['relative h-auto w-full', className])}>
      {headerImage && (
        <div
          style={{
            backgroundImage: `url(${cfSrcForImage(headerImage)})`,
          }}
          className="absolute -top-3 right-0 left-0 -z-2 mx-auto h-[148px] w-[226px] bg-center bg-contain bg-no-repeat"
        />
      )}
      {header}
      <div className="relative w-full">
        {navigationVariant === 'default' && isPrevArrowVisible() && (
          <IconButton
            className="absolute top-1/2 left-0 z-2 -translate-x-1/2 -translate-y-1/2"
            icon="chevron-3.1"
            iconSize={14}
            onClick={onClickPrev}
            size="XL"
            variant="shadowS"
          />
        )}
        <Carousel
          opts={{
            align: 'start',
            slidesToScroll: 5,
            containScroll: 'trimSnaps',
          }}
          setApi={setApi}
          className={carouselBoxClass}
        >
          {background && <BackgroundLayer>{background}</BackgroundLayer>}
          <CarouselContent className={classes(['w-full', contentClassName])}>
            {isLoading && skeletonComponent}
            {items.map((item) => (
              <CarouselItem
                key={item.id}
                className={cn('flex', slideClassName)}
              >
                {item.node}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {navigationVariant === 'default' && isNextArrowVisible() && (
          <IconButton
            className="absolute top-1/2 right-0 z-2 translate-x-1/2 -translate-y-1/2 [&>svg]:rotate-180"
            icon="chevron-3.1"
            iconSize={14}
            onClick={onClickNext}
            size="XL"
            variant="shadowS"
          />
        )}
      </div>
    </div>
  );
};

export default ProductCarouselDesktop;
